import { useState, useEffect } from 'react';
import { IS_LOCAL_DEV } from 'src/constants/envs';

const BUSINESS_HOURS_START = 9;
const BUSINESS_HOURS_END = 18;
const TIMEZONE = 'America/Mexico_City';
const CHECK_INTERVAL_MS = 60_000;

/**
 * In DEV builds, a localStorage flag can override the real business-hours
 * calculation so testers can validate the live and offline advisor screens
 * regardless of the wall clock. Values: 'live' | 'offline' | (anything else
 * falls back to real-time).
 */
export const DEV_BUSINESS_HOURS_OVERRIDE_KEY = 'dev_force_business_hours';
export const DEV_BUSINESS_HOURS_EVENT = 'dev:business-hours-changed';

function readDevOverride(): 'live' | 'offline' | null {
  if (!IS_LOCAL_DEV) return null;
  try {
    const v = localStorage.getItem(DEV_BUSINESS_HOURS_OVERRIDE_KEY);
    if (v === 'live' || v === 'offline') return v;
  } catch {
    /* access denied — fall through to wall clock */
  }
  return null;
}

const getIsLiveSessionAvailable = (): boolean => {
  const override = readDevOverride();
  if (override === 'live') return true;
  if (override === 'offline') return false;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    hour: 'numeric',
    hour12: false,
    weekday: 'short',
  });

  const parts = formatter.formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? -1);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';

  const isWeekday = !['Sat', 'Sun'].includes(weekday);
  // hour < 18 means "live" from 9:00 through 17:59. At 18:00+ the banner switches to offline.
  const isWithinHours = hour >= BUSINESS_HOURS_START && hour < BUSINESS_HOURS_END;

  return isWeekday && isWithinHours;
};

// NOTE: Mexican holidays are not handled in v1. If advisors are unavailable
// on holidays, a backend flag or holiday calendar would be needed.
export const useBusinessHours = (): { isLiveSessionAvailable: boolean } => {
  const [isLiveSessionAvailable, setIsLiveSessionAvailable] = useState(getIsLiveSessionAvailable);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsLiveSessionAvailable(getIsLiveSessionAvailable());
    }, CHECK_INTERVAL_MS);

    if (!IS_LOCAL_DEV) {
      return () => clearInterval(intervalId);
    }

    const onOverrideChange = () => setIsLiveSessionAvailable(getIsLiveSessionAvailable());
    window.addEventListener(DEV_BUSINESS_HOURS_EVENT, onOverrideChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(DEV_BUSINESS_HOURS_EVENT, onOverrideChange);
    };
  }, []);

  return { isLiveSessionAvailable };
};
